import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faFile,
  faFileCode,
  faFileLines,
  faGear,
  faTable,
} from "@fortawesome/free-solid-svg-icons";
import type { PlaygroundProjectFile } from "../auth/api-client";
import { sortProjectFiles } from "./playground-files";

export type PlaygroundFolderNode = {
  readonly kind: "folder";
  readonly name: string;
  readonly path: string;
  readonly children: readonly PlaygroundTreeNode[];
};

export type PlaygroundFileNode = {
  readonly kind: "file";
  readonly path: string;
  readonly name: string;
};

export type PlaygroundTreeNode = PlaygroundFolderNode | PlaygroundFileNode;

const INIT_BASENAME = "__init__.py";

export function isHiddenPlaygroundFile(path: string): boolean {
  const base = path.split("/").pop() ?? path;
  return base === INIT_BASENAME;
}

export function parentFolderPath(filePath: string): string | null {
  const idx = filePath.lastIndexOf("/");
  if (idx < 0) return null;
  return filePath.slice(0, idx);
}

export function joinPlaygroundPath(folder: string | null, name: string): string {
  const trimmed = name.trim().replace(/^\/+|\/+$/g, "");
  if (!folder) return trimmed;
  if (!trimmed) return folder;
  return `${folder}/${trimmed}`;
}

/** Append `.py` when the final path segment has no file extension. */
export function inferPlaygroundFilePath(rawName: string, folderPrefix: string | null): string {
  let path = rawName.trim().replace(/^\/+|\/+$/g, "");
  if (!path) return path;
  if (folderPrefix && !path.startsWith(`${folderPrefix}/`)) {
    path = joinPlaygroundPath(folderPrefix, path);
  }
  const base = path.split("/").pop() ?? path;
  if (!/\.[A-Za-z0-9]+$/.test(base)) {
    path = `${path}.py`;
  }
  return path;
}

export function collectFolderPaths(files: readonly { path: string }[]): Set<string> {
  const folders = new Set<string>();
  for (const f of files) {
    const parts = f.path.split("/");
    for (let i = 1; i < parts.length; i++) {
      folders.add(parts.slice(0, i).join("/"));
    }
  }
  return folders;
}

function pruneOrphanPackageInits(files: readonly PlaygroundProjectFile[]): PlaygroundProjectFile[] {
  return files.filter((f) => {
    if (!isHiddenPlaygroundFile(f.path)) return true;
    // A hidden `__init__.py` is the marker that keeps an (otherwise empty)
    // folder alive, so retain it whenever it lives inside a folder. Only drop a
    // bare root-level `__init__.py`, which corresponds to no folder.
    return parentFolderPath(f.path) !== null;
  });
}

/** Ensures each package folder has a hidden empty `__init__.py` for Python imports. */
export function ensurePlaygroundPackageInits(files: readonly PlaygroundProjectFile[]): PlaygroundProjectFile[] {
  const pruned = pruneOrphanPackageInits(files);
  const byPath = new Map(pruned.map((f) => [f.path, f]));
  for (const f of pruned) {
    if (isHiddenPlaygroundFile(f.path)) continue;
    const parts = f.path.split("/");
    for (let i = 1; i < parts.length; i++) {
      const dir = parts.slice(0, i).join("/");
      const initPath = `${dir}/${INIT_BASENAME}`;
      if (!byPath.has(initPath)) {
        byPath.set(initPath, { path: initPath, content: "" });
      }
    }
  }
  return sortProjectFiles([...byPath.values()]);
}

export function normalizePlaygroundProjectFiles(files: readonly PlaygroundProjectFile[]): PlaygroundProjectFile[] {
  return ensurePlaygroundPackageInits(files);
}

export function visiblePlaygroundFiles(files: readonly PlaygroundProjectFile[]): PlaygroundProjectFile[] {
  return files.filter((f) => !isHiddenPlaygroundFile(f.path));
}

function directChildFolderPaths(parent: string, allFolders: Set<string>, visibleFiles: readonly PlaygroundProjectFile[]): string[] {
  const childNames = new Set<string>();
  const prefix = parent ? `${parent}/` : "";

  for (const folder of allFolders) {
    if (parent) {
      if (!folder.startsWith(prefix) || folder === parent) continue;
      const rest = folder.slice(prefix.length);
      const first = rest.split("/")[0];
      if (first) childNames.add(`${parent}/${first}`);
    } else if (folder.includes("/")) {
      childNames.add(folder.split("/")[0]!);
    } else {
      childNames.add(folder);
    }
  }

  for (const f of visibleFiles) {
    const rel = parent ? (f.path.startsWith(prefix) ? f.path.slice(prefix.length) : null) : f.path;
    if (rel === null) continue;
    const parts = rel.split("/");
    if (parts.length > 1) {
      childNames.add(parent ? `${parent}/${parts[0]}` : parts[0]!);
    }
  }

  return [...childNames].sort((a, b) => a.localeCompare(b));
}

function directChildFileNodes(parent: string, visibleFiles: readonly PlaygroundProjectFile[]): PlaygroundFileNode[] {
  const prefix = parent ? `${parent}/` : "";
  return visibleFiles
    .filter((f) => {
      if (parent) {
        if (!f.path.startsWith(prefix)) return false;
        return !f.path.slice(prefix.length).includes("/");
      }
      return !f.path.includes("/");
    })
    .map((f) => ({ kind: "file" as const, path: f.path, name: f.path.split("/").pop()! }))
    .sort((a, b) => {
      if (a.path === "main.py") return -1;
      if (b.path === "main.py") return 1;
      return a.name.localeCompare(b.name);
    });
}

function buildFolderNode(folderPath: string, allFolders: Set<string>, visibleFiles: readonly PlaygroundProjectFile[]): PlaygroundFolderNode {
  const name = folderPath.split("/").pop() ?? folderPath;
  const childFolders = directChildFolderPaths(folderPath, allFolders, visibleFiles).map((fp) =>
    buildFolderNode(fp, allFolders, visibleFiles)
  );
  const childFiles = directChildFileNodes(folderPath, visibleFiles);
  return { kind: "folder", name, path: folderPath, children: [...childFolders, ...childFiles] };
}

export function buildPlaygroundFileTree(files: readonly PlaygroundProjectFile[]): PlaygroundTreeNode[] {
  const visibleFiles = visiblePlaygroundFiles(files);
  const allFolders = collectFolderPaths(files);
  const rootFolders = directChildFolderPaths("", allFolders, visibleFiles).map((fp) =>
    buildFolderNode(fp, allFolders, visibleFiles)
  );
  const rootFiles = directChildFileNodes("", visibleFiles);
  return [...rootFolders, ...rootFiles];
}

export function renamePlaygroundFolderPaths(
  files: readonly PlaygroundProjectFile[],
  fromFolder: string,
  toFolder: string
): PlaygroundProjectFile[] {
  const prefix = `${fromFolder}/`;
  return files.map((f) => {
    if (f.path === fromFolder || f.path.startsWith(prefix)) {
      const rest = f.path === fromFolder ? "" : f.path.slice(prefix.length);
      const nextPath = rest ? `${toFolder}/${rest}` : toFolder;
      return { ...f, path: nextPath };
    }
    return f;
  });
}

export function deletePlaygroundFolderPaths(
  files: readonly PlaygroundProjectFile[],
  folderPath: string
): PlaygroundProjectFile[] {
  const prefix = `${folderPath}/`;
  return files.filter((f) => f.path !== folderPath && !f.path.startsWith(prefix));
}

export function playgroundFileIcon(path: string): IconDefinition {
  const ext = (path.split(".").pop() ?? "").toLowerCase();
  switch (ext) {
    case "py":
    case "pyw":
      return faFileCode;
    case "txt":
    case "md":
    case "markdown":
    case "rst":
      return faFileLines;
    case "html":
    case "htm":
    case "css":
    case "js":
    case "ts":
    case "json":
      return faFileCode;
    case "csv":
    case "tsv":
      return faTable;
    case "toml":
    case "yaml":
    case "yml":
    case "ini":
    case "cfg":
    case "env":
      return faGear;
    default:
      return faFile;
  }
}
